import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { Lock, DollarSign, AlertTriangle } from 'lucide-react';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';

export const BatteryAllocationGauge = () => {
  const { data: plants, isLoading } = useGetPlantsQuery({ includeOverview: true });

  const availableEnergy =
    plants?.reduce((sum, p) => {
      if (!p?.technicalSpecifications.storageCapacity || !p?.batteryStateOfCharge) {
        return sum;
      }
      return (
        sum + (+p?.technicalSpecifications.storageCapacity * +p.batteryStateOfCharge) / 100
      );
    }, 0) || 0;

  const allocation = {
    total: availableEnergy,
    peakShaving: availableEnergy * 0.2,
    minReserve: availableEnergy * 0.1,
    trading: availableEnergy * 0.7,
  };

  const pct = (value: number) => `${(value / allocation.total) * 100}%`;

  return (
    <DashboardCard isLoading={isLoading} className="min-h-[362px]">
      <div className="mb-4 text-base font-semibold">Battery Allocation</div>
      <div className="mb-3 text-base">
        <div className="mb-1 text-zinc-500">Total Available Energy</div>
        <div className="text-lg font-semibold">{allocation.total.toFixed(0)} kWh</div>
      </div>
      <div className="flex flex-col gap-2.5 text-base">
        <div>
          <div className="mb-1 flex items-center gap-1.5">
            <Lock size={14} color="#ef4444" />
            <span className="text-zinc-500">Reserved (Peak-shaving)</span>
          </div>
          <div className="font-semibold">{allocation.peakShaving.toFixed(0)} kWh</div>
          <div className="mt-1 h-1.5 rounded bg-red-100">
            <div
              className="h-full rounded bg-red-500"
              style={{ width: pct(allocation.peakShaving) }}
            />
          </div>
        </div>
        <div>
          <div className="mb-1 flex items-center gap-1.5">
            <DollarSign size={14} color="#22c55e" />
            <span className="text-zinc-500">Available for Trading</span>
          </div>
          <div className="font-semibold text-green-500">
            {allocation.trading.toFixed(0)} kWh
          </div>
          <div className="mt-1 h-1.5 rounded bg-green-100">
            <div
              className="h-full rounded bg-green-500"
              style={{ width: pct(allocation.trading) }}
            />
          </div>
        </div>
        <div>
          <div className="mb-1 flex items-center gap-1.5">
            <AlertTriangle size={14} color="#f59e0b" />
            <span className="text-zinc-500">Min Operating Reserve</span>
          </div>
          <div className="font-semibold">{allocation.minReserve.toFixed(0)} kWh</div>
          <div className="mt-1 h-1.5 rounded bg-amber-100">
            <div
              className="h-full rounded bg-amber-500"
              style={{ width: pct(allocation.minReserve) }}
            />
          </div>
        </div>
      </div>
    </DashboardCard>
  );
};

export default BatteryAllocationGauge;
